/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { CartIconComponent, WishListIconComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    format,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    KeyCodes,
    Modal,
    ModalBody,
    ModalHeader,
    onTelemetryClick,
    Popover,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { HeaderPreferredStore, Logo } from './components';
import { INosHeaderData } from './nos-header.data';
import { INosHeaderProps, INosHeaderResources, IMyAccountLinksData } from './nos-header.props.autogenerated';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
    hasSigninerror?: boolean;
}

export interface IHeaderViewProps extends INosHeaderProps<INosHeaderData>, IHeaderState {
    logo: React.ReactNode;
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    menuBar: React.ReactNode[];
    className: string;
    search: React.ReactNode[];
    siteOptions: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;
    Divider: INodeProps;

    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    switchCustomerLink?: React.ReactNode;
    isOBORequest?: boolean;
    accountLinks?: React.ReactNode[];
    preferredStore?: React.ReactNode;
    shouldHideShowSignInSignOutLink?: boolean;
}

/**
 *
 * Header component.
 * @extends {React.PureComponent<INosHeaderProps<INosHeaderData>>}
 */
@observer
class Header extends React.PureComponent<INosHeaderProps<INosHeaderData>, IHeaderState> {
    private readonly popOverRef: React.RefObject<HTMLButtonElement>;

    private readonly telemetryContent: ITelemetryContent;

    private cart: ICartState | undefined;

    /**
     * The signed-in user name displayed in the header bar
     */
    @computed public get displayName(): Readonly<string | undefined> {
        const user = this.props.context.request.user;
        return user?.isOBORequest ? this.workerName : this.customerName;
    }

    /**
     * OBO scenario. The name of worker who shops on behalf of customer.
     */
    @computed public get workerName(): Readonly<string | undefined> {
        const user = this.props.context.request.user;
        return user?.firstName || user?.name;
    }

    @computed public get customerName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    public constructor(props: INosHeaderProps<INosHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false,
            hasSigninerror: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('keydown', this._keydown);
        }
        reaction(
            () => this.props.data.cart.result,
            () => {
                this.cart = this.props.data.cart.result;
            }
        );

        const {
            context: {
                request: { user }
            },
            resources: { signUpCustomerNotFoundTitle, signUpMultipleCustomerFoundTitle, closeNotificationLabel }
        } = this.props;

        const signinNotificationError = this._getSigninNotification(user, signUpMultipleCustomerFoundTitle, signUpCustomerNotFoundTitle);
        if (signinNotificationError !== undefined && this.state.hasSigninerror === false) {
            // For signup show simple error notification.
            NotificationsManager.instance().addNotification(new ErrorNotification(signinNotificationError, closeNotificationLabel));
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('keydown', this._keydown, false);
        }
    }

    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: { accountInformation, wishlists },
            context: {
                request: { user }
            },
            context,
            resources: { cartLabel, cartQtyLabel }
        } = this.props;
        const { logoLink, logoImage, shouldShowWishlistCount, shouldHideShowSignInSignOutLink } = this.props.config;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- app config is generic
        const disableTooltip = this.props.context.app.config.disableTooltip || false;
        const headerToggleClass = this.state.mobileMenuCollapsed ? '' : 'nos-header-open';
        const headerClassName = classnames('nos-header', this.props.config.className, headerToggleClass);
        const navbarKey = 'header-nav-mobile';
        const customer = accountInformation && accountInformation.result;
        const cartIconSlot = this._getSlot('cartIcon');
        const menuBarSlot = this._getSlot('menuBar');
        const searchSlot = this._getSlot('search');
        let wishlistCount = 0;
        const siteOptionsSlot = this._getSlot('siteOptions');
        const preferredStoreSlot = this._getSlot('storeSelector');
        if (wishlists !== undefined) {
            if (
                !wishlists.result ||
                !ArrayExtensions.hasElements(wishlists.result) ||
                !wishlists.result[0].CommerceListLines ||
                !ArrayExtensions.hasElements(wishlists.result[0].CommerceListLines)
            ) {
                wishlistCount = 0;
            } else {
                wishlistCount = wishlists.result[0].CommerceListLines.length;
            }
        }
        const viewProps: IHeaderViewProps = {
            ...(this.props as INosHeaderProps<INosHeaderData>),
            ...this.state,
            shouldHideShowSignInSignOutLink,
            logo: (
                <Logo
                    {...{
                        link: logoLink,
                        image: logoImage,
                        className: 'nos-header__logo',
                        gridSettings: this.props.context.request.gridSettings,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent,
                        typeName: this.props.typeName
                    }}
                />
            ),
            wishListIconDesktop: (
                <WishListIconComponent
                    className='nos-header__wishlist-desktop'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                    wishlistCount={wishlistCount}
                    wishlistCountLabel={this.props.resources.wishlistCountLabel}
                    isDispayWishlistCount={shouldShowWishlistCount}
                />
            ),
            wishListIconMobile: (
                <WishListIconComponent
                    className='nos-header__wishlist-mobile'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                    wishlistCount={wishlistCount}
                    wishlistCountLabel={this.props.resources.wishlistCountLabel}
                    isDispayWishlistCount={shouldShowWishlistCount}
                />
            ),
            cartIcon: ArrayExtensions.hasElements(cartIconSlot) ? (
                cartIconSlot[0]
            ) : (
                <CartIconComponent
                    cartLabel={cartLabel}
                    cartQtyLabel={cartQtyLabel}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{ cart: this.cart }}
                />
            ),

            search: ArrayExtensions.hasElements(searchSlot) ? searchSlot : [],
            siteOptions: ArrayExtensions.hasElements(siteOptionsSlot) ? siteOptionsSlot : [],
            className: headerClassName,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                'aria-label': this.props.resources.headerAriaLabel,
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('nos-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('nos-header__topbar')
            },
            Divider: {
                className: classnames('nos-header__divider')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'nos-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: 'nos-header__modal',
                toggle: this._toggleNavbar
            },
            MobileMenuHeader: <ModalHeader className='nos-header__mobile-hamburger-menu-header' toggle={this._toggleNavbar} />,
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'nos-header__mobile-hamburger-menu-body'
            },
            MobileMenuLinksContainer: {
                className: 'nos-header__mobile-hamburger-menu-links'
            },
            AccountInfoDropdownParentContainer: {
                className: 'nos-header__account-info'
            },
            AccountInfoDropdownPopoverConentContainer: this.displayName
                ? {
                      tag: Popover,
                      id: 'myprofilePopover',
                      className: 'nos-header__account-info-content',
                      placement: 'bottom',
                      isOpen: this.state.signinPopoverOpen,
                      target: this.popOverRef,
                      toggle: this._togglePopover
                  }
                : undefined,
            signInLink: this._getSigninButton(user, this.props.resources),
            signOutLink: this._getSignOutButton(user, this.props.resources),
            isOBORequest: user?.isOBORequest,
            switchCustomerLink: this._getSwitchCustomerButton(user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(user, this.props.resources),
            menuBar: ArrayExtensions.hasElements(menuBarSlot) ? menuBarSlot : [],
            accountLinks: this._getAccountLinks(customer),
            preferredStore: ArrayExtensions.hasElements(preferredStoreSlot) && <HeaderPreferredStore {...this.props} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle link text change.
     * @param linkIndex - Link Index.
     * @returns : Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.myAccountLinks?.[linkIndex]) {
            this.props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**
     * Get the slot.
     * @param slotName - Name of the slot.
     * @returns The slot.
     */
    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    /**
     * Renders the sign in button.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getSigninButton(user: MsDyn365.IRequestContextUser | undefined, resources: INosHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return (
            !this.displayName && (
                <Button
                    className='nos-header__signin-button'
                    title={resources.signInLinkText}
                    href={user?.signInUrl}
                    aria-label={resources.signInLinkAriaText}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign in')}
                    {...attributes}
                >
                    <span className='nos-header__signin-button-text' aria-hidden>
                        {resources.signInLinkText}
                    </span>
                </Button>
            )
        );
    }

    /**
     * Renders the sign out button.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getSignOutButton(user: MsDyn365.IRequestContextUser | undefined, resources: INosHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return (
            this.displayName && (
                <Button
                    className='nos-header__signout-button'
                    title={resources.signOutLinkText}
                    href={user?.signOutUrl ?? ''}
                    aria-label={resources.signOutLinkAriaText}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign out')}
                    {...attributes}
                >
                    <span className='nos-header__signout-button-text' aria-hidden>
                        {resources.signOutLinkText}
                    </span>
                </Button>
            )
        );
    }

    /**
     * Renders the sign out button.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getSwitchCustomerButton(
        user: MsDyn365.IRequestContextUser | undefined,
        resources: INosHeaderResources
    ): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SwitchCustomer);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const isOBOAccountSelected = user?.isOBORequest && this.props.context.request.cookies.getAccountSelectionCookie();
        return (
            this.displayName &&
            isOBOAccountSelected && (
                <MsDyn365.Link
                    link={{
                        ariaLabel: resources.switchCustomerLinkAriaText,
                        className: 'nos-header__switchcustomer-button',
                        linkText: resources.switchCustomerLinkText,
                        linkUrl: MsDyn365.getUrlSync('businessPartnerSelection', this.props.context.actionContext),
                        role: 'link',
                        onClick: () => this.props.context.request.cookies.removeAccountSelectionCookie(),
                        additionalProperties: attributes
                    }}
                />
            )
        );
    }

    private _getSigninNotification(
        user: MsDyn365.IRequestContextUser | undefined,
        signUpMultipleCustomerFoundTitle: string,
        signUpCustomerNotFoundTitle: string
    ): string | undefined {
        const { signUpCustomerNotFound, signUpMultipleCustomerFound } = this.props.config;
        const notFoundError = 'Microsoft_Dynamics_Commerce_Runtime_CustomerNotFound_WhenAutoLinking';
        const multipleAccountError = 'Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountsFoundWithSameEmailAddress';

        if (user?.retailServerErrorCode === notFoundError) {
            return signUpCustomerNotFound !== undefined ? signUpCustomerNotFound : signUpCustomerNotFoundTitle;
        } else if (user?.retailServerErrorCode === multipleAccountError) {
            return signUpMultipleCustomerFound !== undefined ? signUpMultipleCustomerFound : signUpMultipleCustomerFoundTitle;
        }
        return undefined;
    }

    private _getAccountInfoDropdownButton(
        user: MsDyn365.IRequestContextUser | undefined,
        resources: INosHeaderResources
    ): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return (
            this.displayName && (
                <Button
                    innerRef={this.popOverRef}
                    className='nos-header__profile-button'
                    aria-describedby='myprofilePopover'
                    onClick={this._togglePopover}
                    color='link'
                    aria-label={this.displayName}
                    aria-expanded={this.state.signinPopoverOpen}
                    {...attributes}
                >
                    <div className='nos-profile-button-text'>
                        <div>{this.displayName}</div>
                        {user?.isOBORequest && this.customerName && <div>{format(resources.shoppingAsText, this.customerName)}</div>}
                    </div>
                </Button>
            )
        );
    }

    // private _getHeaderLinks = (links?: IHeaderLinksData[], menuBarSlot?: any): React.ReactNode[] | undefined => {
    //     return links?.map((link, index) => {
    //         if (!link.opensMenu) {
    //             const editableLink: MsDyn365.ILinksData = {
    //                 ariaLabel: link.ariaLabel,
    //                 className: 'nos-header__header-link',
    //                 linkText: link.linkText,
    //                 linkUrl: link.linkUrl.destinationUrl,
    //                 openInNewTab: false,
    //                 role: 'link'
    //             };
    //             return (
    //                 <MsDyn365.Link
    //                     key={index}
    //                     link={editableLink}
    //                     editProps={{ onTextChange: this.handleLinkTextChange(index), requestContext: this.props.context.request }}
    //                 />
    //             );
    //         } else {
    //             // TODO: Handle Productlink with navigation menu.
    //             return ArrayExtensions.hasElements(menuBarSlot) ? menuBarSlot : [];
    //         }
    //     });
    // };

    private _getAccountLinks(customer: Customer | undefined): React.ReactNode[] | undefined {
        const { myAccountLinks } = this.props.config;
        const payLoad = getPayloadObject('click', this.telemetryContent, '');
        if (!customer || !this.displayName || !myAccountLinks || !ArrayExtensions.hasElements(myAccountLinks)) {
            return undefined;
        }
        return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
            payLoad.contentAction.etext = cta.linkText;
            const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: cta.ariaLabel,
                className: 'nos-signin-info__account-link-button',
                linkText: cta.linkText,
                linkUrl: cta.linkUrl.destinationUrl,
                openInNewTab: cta.openInNewTab,
                role: 'link',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={index}
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange(index), requestContext: this.props.context.request }}
                />
            );
        });
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
    }

    private _togglePopover(): void {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    }

    private _keydown(event: KeyboardEvent): void {
        if (event.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
        }
    }
}

export default Header;
